import { useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import Loader from "../../components/Loader";
import { CustomDatePicker } from "../../components/UI/CustomDatePicker";
import useGetPatientEdiary from "../../hooks/useGetPatientEdiary";
import useUpdateEdiaryStartDateMutation from "../../hooks/useUpdateEdiaryStartDateMutation";
import { formatDate } from "../../utils/helpers";

dayjs.extend(utc);

const STATUS_COLORS = {
  Completed: "success",
  InComplete: "warning",
  Skipped: "error",
  "Not Opened": "default",
};

const STATUS_LABELS = {
  InComplete: "Open",
};

/**
 * A subject's eDiaries: one row per occurrence, a click on an answered one opens its answers.
 * With showSchedule (the eDiary tab of the eCRF visit tabs) it also shows the schedule summary and
 * the start date editor; the eDiary menu page shows the table only.
 */
const PatientEdiaryPanel = ({ patientId, trialId, isTrialLocked = false, showSchedule = true }) => {
  const navigate = useNavigate();

  const { data, isLoading } = useGetPatientEdiary({
    patientId,
    trialId,
    enabled: !!trialId,
  });
  const updateStartDate = useUpdateEdiaryStartDateMutation();

  const [newStartDate, setNewStartDate] = useState("");

  const occurrences = data?.occurrences || [];
  const canEditStartDate = showSchedule && Boolean(data?.can_edit_start_date) && !isTrialLocked;

  const handleSaveStartDate = () => {
    if (!newStartDate) return;
    // Local midnight of the chosen day, sent in UTC (same as the Schedule Baseline date)
    const startDate = dayjs(newStartDate).startOf("day").utc().toISOString();
    updateStartDate.mutate(
      { patientId, trialId, startDate },
      { onSuccess: () => setNewStartDate("") }
    );
  };

  const openAnswers = (sessionId, label) => {
    navigate(`/patient/${patientId}/ediary/${sessionId}?trial=${trialId}`, {
      state: { visitName: label },
    });
  };

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  if (isLoading) return <Loader />;

  if (!data) {
    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6">eDiary</Typography>
        <Typography color="text.secondary">This subject has no eDiary in this trial.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 2 }} data-testid="ediary-panel">
      <Stack spacing={2} mb={2}>
        <Typography variant="h6">{data.name}</Typography>

        {showSchedule && (
          <Stack
            direction="row"
            spacing={3}
            flexWrap="wrap"
            useFlexGap
            data-testid="ediary-schedule-summary"
          >
            <Typography variant="body2">
              <strong>Start date:</strong> {formatDate(data.ediary_start_date)}
            </Typography>
            <Typography variant="body2">
              <strong>Completed:</strong> {data.completed} / {data.total}
            </Typography>
            <Typography variant="body2">
              <strong>Missed:</strong> {data.skipped}
            </Typography>
            <Typography variant="body2">
              <strong>Every:</strong> {data.config.interval_hours} h, open for{" "}
              {data.config.window_hours} h
            </Typography>
          </Stack>
        )}

        {canEditStartDate && (
          <Box data-testid="ediary-start-date-editor">
            <Alert severity="info" sx={{ mb: 1 }}>
              Changing the start date moves every eDiary of this subject. Completed eDiaries stay
              completed, and missed ones are reopened.
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ minWidth: 220 }}>
                <CustomDatePicker
                  mode="date"
                  label="New start date"
                  value={newStartDate}
                  onChange={setNewStartDate}
                />
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveStartDate}
                data-testid="ediary-update-start-date-btn"
                disabled={!newStartDate || updateStartDate.isLoading}
              >
                Update start date
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "80vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>#</TableCell>
              <TableCell sx={headerCellSx}>eDiary</TableCell>
              <TableCell sx={headerCellSx}>Opens</TableCell>
              <TableCell sx={headerCellSx}>Closes</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Completed On</TableCell>
              <TableCell sx={headerCellSx}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {occurrences.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No eDiaries have been scheduled for this subject.
                </TableCell>
              </TableRow>
            )}
            {occurrences.map((occurrence) => (
              <TableRow
                key={occurrence.session_id}
                hover={occurrence.answered}
                selected={occurrence.is_current}
                sx={{ cursor: occurrence.answered ? "pointer" : "default" }}
                onClick={
                  occurrence.answered
                    ? () => openAnswers(occurrence.session_id, occurrence.label)
                    : undefined
                }
                data-testid={`ediary-row-${occurrence.occurrence_number}`}
                data-status={occurrence.status}
              >
                <TableCell>{occurrence.occurrence_number}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{occurrence.label}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(occurrence.open_date)}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(occurrence.close_date)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLORS[occurrence.status] || "default"}
                    label={STATUS_LABELS[occurrence.status] || occurrence.status}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(occurrence.completed_at)}
                </TableCell>
                <TableCell>
                  {occurrence.answered && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(event) => {
                        event.stopPropagation();
                        openAnswers(occurrence.session_id, occurrence.label);
                      }}
                    >
                      View answers
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PatientEdiaryPanel;
